import { animate, style, transition, trigger } from '@angular/animations';
import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NbGlobalPosition, NbLayoutDirectionService, NbPositionHelper, NbGlobalPhysicalPosition } from '@nebular/theme';

import { TOASTR_CONFIG } from '../core/classes';
import { TInsertedToast } from '../core/toast.types';

const voidState = style({
  transform: 'translateX({{ direction }}110%)',
  height: 0,
  marginLeft: '0',
  marginRight: '0',
  marginTop: '0',
  marginBottom: '0',
});

const defaultOptions = { params: { direction: '' } };

/* eslint-disable @typescript-eslint/member-ordering */
@Component({
  selector: 'sui-toastr-container',
  imports: [NgComponentOutlet],
  templateUrl: './toast-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [voidState, animate(100)], defaultOptions),
      transition(':leave', [animate(100, voidState)], defaultOptions),
    ]),
  ],
  host: {
    '[class]': 'config?.containerClass',
  },
})
export class ToastContainerComponent implements OnInit {
  private readonly layoutDirection = inject(NbLayoutDirectionService);

  private readonly positionHelper = inject(NbPositionHelper);

  private readonly destroyRef = inject(DestroyRef);

  protected readonly config = inject(TOASTR_CONFIG);

  protected readonly fadeIn = signal<Record<string, unknown> | null>(null);

  readonly content = signal<TInsertedToast[]>([]);

  readonly context = signal<Object>({});

  readonly position = signal<NbGlobalPosition>(NbGlobalPhysicalPosition.TOP_RIGHT);

  readonly components = viewChildren(NgComponentOutlet);

  ngOnInit() {
    this.layoutDirection
      .onDirectionChange()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onDirectionChange());
  }

  getToastInstance(id: string): any | null {
    if (!this.components()?.length) {
      return null;
    }

    const idx = this.content().findIndex((i) => {
      return i.id === id;
    });

    if (idx === -1) return null;

    // TODO: Refactor after upgrading to Angular 19
    return ((this.components().at(idx) as any)['_componentRef']['instance'] as any) || null;
  }

  protected onDirectionChange() {
    const direction = this.positionHelper.isRightPosition(this.position()) ? '' : '-';

    this.fadeIn.set({ value: '', params: { direction } });
  }
}
